import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Package, Plus, Edit, Trash2, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

function AdminBundlesPage() {
  const [bundles, setBundles] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBundle, setEditingBundle] = useState(null);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    original_price: '',
    age_groups: [],
    product_ids: [],
    image_url: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [bundlesRes, productsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bundles`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products`)
      ]);

      const bundlesData = await bundlesRes.json();
      const productsData = await productsRes.json();

      setBundles(bundlesData.bundles || []);
      setProducts(productsData.products || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBundle = () => {
    setEditingBundle(null);
    setFormData({
      name: '',
      description: '',
      price: '',
      original_price: '',
      age_groups: [],
      product_ids: [],
      image_url: ''
    });
    setDialogOpen(true);
  };

  const handleEditBundle = (bundle) => {
    setEditingBundle(bundle);
    setFormData({
      name: bundle.name,
      description: bundle.description,
      price: bundle.price.toString(),
      original_price: bundle.original_price.toString(),
      age_groups: bundle.age_groups || [],
      product_ids: bundle.product_ids || [],
      image_url: bundle.image_url || ''
    });
    setDialogOpen(true);
  };

  const handleSaveBundle = async () => {
    try {
      const price = parseFloat(formData.price);
      const original_price = parseFloat(formData.original_price);

      if (!formData.name || !price || !original_price) {
        toast.error('Please fill in all required fields');
        return;
      }

      const bundleData = {
        name: formData.name,
        description: formData.description,
        price,
        original_price,
        savings: original_price - price,
        age_groups: formData.age_groups,
        product_ids: formData.product_ids,
        image_url: formData.image_url,
        category: 'bundle'
      };

      const url = editingBundle 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/bundles/${editingBundle.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/bundles`;
      
      const method = editingBundle ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bundleData)
      });

      if (response.ok) {
        toast.success(editingBundle ? 'Bundle updated!' : 'Bundle created!');
        setDialogOpen(false);
        fetchData();
      } else {
        toast.error('Failed to save bundle');
      }
    } catch (error) {
      console.error('Failed to save bundle:', error);
      toast.error('Failed to save bundle');
    }
  };

  const handleDeleteBundle = async (bundleId) => {
    if (!window.confirm('Are you sure you want to delete this bundle? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bundles/${bundleId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Bundle deleted successfully');
        fetchData();
      } else {
        toast.error('Failed to delete bundle');
      }
    } catch (error) {
      console.error('Failed to delete bundle:', error);
      toast.error('Failed to delete bundle');
    }
  };

  const handleGenerateAIBundles = async () => {
    setAiGenerating(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bundles/generate-ai`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ count: 3 })
      });

      const data = await response.json();
      
      if (data.suggestions && data.suggestions.length > 0) {
        setAiSuggestions(data.suggestions);
        toast.success(`AI generated ${data.suggestions.length} bundle suggestions!`, {
          description: 'Review and create the ones you like'
        });
      } else {
        toast.info('No bundle suggestions generated', {
          description: 'Try adding more products first'
        });
      }
    } catch (error) {
      console.error('Failed to generate AI bundles:', error);
      toast.error('Failed to generate AI bundles');
    } finally {
      setAiGenerating(false);
    }
  };

  const handleCreateFromAISuggestion = async (suggestion) => {
    try {
      const bundleData = {
        name: suggestion.name,
        description: suggestion.description,
        price: suggestion.price,
        original_price: suggestion.original_price,
        age_groups: suggestion.age_groups,
        product_ids: suggestion.product_ids,
        image_url: suggestion.image_url
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bundles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bundleData)
      });

      if (response.ok) {
        toast.success('Bundle created from AI suggestion!');
        fetchData();
        // Remove from suggestions
        setAiSuggestions(prev => prev.filter(s => s.name !== suggestion.name));
      }
    } catch (error) {
      console.error('Failed to create bundle:', error);
      toast.error('Failed to create bundle');
    }
  };

  const toggleProduct = (productId) => {
    setFormData(prev => ({
      ...prev,
      product_ids: prev.product_ids.includes(productId)
        ? prev.product_ids.filter(id => id !== productId)
        : [...prev.product_ids, productId]
    }));
  };

  const toggleAgeGroup = (age) => {
    setFormData(prev => ({
      ...prev,
      age_groups: prev.age_groups.includes(age)
        ? prev.age_groups.filter(a => a !== age)
        : [...prev.age_groups, age]
    }));
  };

  const ageOptions = ['0-2', '3-5', '6-12', '12+'];

  return (
    <div className="min-h-screen py-4 sm:py-6 px-3 sm:px-4 pb-20 md:pb-8">
      <Helmet>
        <title>Manage Bundles - Admin | Mothering</title>
      </Helmet>

      <div className="container mx-auto max-w-7xl">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-4 sm:mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold flex items-center gap-2">
              <Package className="w-6 h-6 sm:w-8 sm:h-8 text-cyan-600" />
              Manage Bundles
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground mt-1">Create and manage product bundles</p>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button 
              onClick={handleGenerateAIBundles} 
              variant="outline"
              className="gap-2 flex-1 sm:flex-none"
              disabled={aiGenerating}
            >
              {aiGenerating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  AI Suggestions
                </>
              )}
            </Button>
            <Button onClick={handleCreateBundle} className="gap-2 bg-cyan-600 hover:bg-cyan-700 flex-1 sm:flex-none">
              <Plus className="w-4 h-4" />
              Create Bundle
            </Button>
          </div>
        </div>

        {/* AI Suggestions Section */}
        {aiSuggestions.length > 0 && (
          <Card className="mb-6 border-purple-200 bg-purple-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-purple-700">
                <Sparkles className="w-5 h-5" />
                AI Bundle Suggestions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {aiSuggestions.map((suggestion, idx) => (
                  <Card key={idx} className="bg-white">
                    <CardContent className="p-4">
                      <h3 className="font-semibold mb-2">{suggestion.name}</h3>
                      <p className="text-sm text-muted-foreground mb-3">
                        {suggestion.description}
                      </p>
                      <div className="space-y-2 text-sm mb-3">
                        <div className="flex justify-between">
                          <span>Price:</span>
                          <span className="font-semibold">₹{suggestion.price}</span>
                        </div>
                        <div className="flex justify-between text-green-600">
                          <span>Savings:</span>
                          <span className="font-semibold">₹{suggestion.savings} ({suggestion.discount_percent}% OFF)</span>
                        </div>
                        <div className="text-xs text-muted-foreground italic">
                          {suggestion.ai_reasoning}
                        </div>
                      </div>
                      <Button 
                        size="sm" 
                        className="w-full"
                        onClick={() => handleCreateFromAISuggestion(suggestion)}
                      >
                        Create This Bundle
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-64 bg-gray-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : bundles.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Package className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-base sm:text-lg font-semibold mb-2">No bundles yet</h3>
              <p className="text-sm sm:text-base text-muted-foreground mb-4">Create your first bundle to get started</p>
              <Button onClick={handleCreateBundle}>Create Bundle</Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {bundles.map((bundle) => (
              <Card key={bundle.id}>
                <CardHeader className="p-4 sm:p-6">
                  <CardTitle className="text-base sm:text-lg flex items-center justify-between gap-2">
                    <span className="truncate">{bundle.name}</span>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleEditBundle(bundle)}
                        title="Edit bundle"
                      >
                        <Edit className="w-3 h-3 sm:w-4 sm:h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleDeleteBundle(bundle.id)}
                        title="Delete bundle"
                      >
                        <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 text-red-500" />
                      </Button>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 sm:p-6 pt-0">
                  <p className="text-xs sm:text-sm text-muted-foreground mb-3 line-clamp-2">
                    {bundle.description}
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-lg sm:text-xl font-bold text-cyan-600">₹{bundle.price}</span>
                      <span className="text-xs sm:text-sm text-gray-400 line-through">₹{bundle.original_price}</span>
                      <Badge variant="secondary" className="bg-green-100 text-green-700 text-xs">
                        Save ₹{bundle.savings}
                      </Badge>
                    </div>
                    {bundle.age_groups && bundle.age_groups.length > 0 && (
                      <div className="flex gap-1 flex-wrap">
                        {bundle.age_groups.map((age) => (
                          <Badge key={age} variant="outline" className="text-xs">
                            {age} yrs
                          </Badge>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {bundle.product_ids?.length || 0} products included
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingBundle ? 'Edit Bundle' : 'Create New Bundle'}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="name">Bundle Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Back to School Kit"
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Everything your child needs for school..."
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="price">Bundle Price * (₹)</Label>
                  <Input
                    id="price"
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="1999"
                  />
                </div>
                <div>
                  <Label htmlFor="original_price">Original Price * (₹)</Label>
                  <Input
                    id="original_price"
                    type="number"
                    value={formData.original_price}
                    onChange={(e) => setFormData({ ...formData, original_price: e.target.value })}
                    placeholder="2499"
                  />
                </div>
              </div>

              {formData.price && formData.original_price && (
                <div className="text-sm text-green-600 font-semibold">
                  Savings: ₹{parseFloat(formData.original_price) - parseFloat(formData.price)}
                </div>
              )}

              <div>
                <Label>Age Groups</Label>
                <div className="flex gap-2 mt-2">
                  {ageOptions.map((age) => (
                    <Button
                      key={age}
                      type="button"
                      variant={formData.age_groups.includes(age) ? "default" : "outline"}
                      size="sm"
                      onClick={() => toggleAgeGroup(age)}
                    >
                      {age} yrs
                    </Button>
                  ))}
                </div>
              </div>

              <div>
                <Label>Select Products ({formData.product_ids.length} selected)</Label>
                <div className="border rounded-lg p-3 max-h-60 overflow-y-auto mt-2">
                  {products.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No products available</p>
                  ) : (
                    <div className="space-y-2">
                      {products.map((product) => (
                        <div
                          key={product.id}
                          className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                            formData.product_ids.includes(product.id)
                              ? 'bg-cyan-50 border border-cyan-200'
                              : 'hover:bg-gray-50'
                          }`}
                          onClick={() => toggleProduct(product.id)}
                        >
                          <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                            <img
                              src={product.image_urls?.[0] || 'https://via.placeholder.com/40'}
                              alt={product.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{product.title}</p>
                            <p className="text-xs text-muted-foreground">₹{product.price}</p>
                          </div>
                          {formData.product_ids.includes(product.id) && (
                            <Badge className="bg-cyan-600">Selected</Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveBundle} className="bg-cyan-600 hover:bg-cyan-700">
                {editingBundle ? 'Update Bundle' : 'Create Bundle'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

export default AdminBundlesPage;
